import type { EChartsOption } from 'echarts';
import type { EChartsTheme } from '../types';
import { buildAnimationConfig } from '../shared/animation';
import { buildTooltipConfig } from '../shared/tooltip';
import { buildSidewaysBarSeries } from './series';
import type { SidewaysBarDatum, SidewaysBarChartConfig } from './types';

export type { SidewaysBarDatum, SidewaysBarChartConfig } from './types';

// Mirrors CSS `text-transform: capitalize` (amcharts4 category label adapter):
// uppercase the first letter of each word.
const capitalize = (text: string): string => text.replace(/\b\w/g, (c) => c.toUpperCase());

const buildTooltipFormatter = (data: SidewaysBarDatum[], theme: EChartsTheme, config: SidewaysBarChartConfig) => {
  const { privacyMask, simpleTooltip = false } = config;

  return (params: unknown): string => {
    const p = (Array.isArray(params) ? params[0] : params) as { dataIndex?: number };
    const datum = typeof p?.dataIndex === 'number' ? data[p.dataIndex] : undefined;
    if (!datum) return '';

    const amount = String(privacyMask(datum.tooltipAmount));

    if (simpleTooltip) {
      return `${capitalize(datum.category)}: ${amount}`;
    }

    // Matches amcharts4 tooltipHTML: colored dot + category + amount.
    return `<div style="min-width:170px;display:grid;grid-column-gap:10px;color:${theme.textColor};border-radius:${theme.tooltipBorderRadius}px;">
        <div style="max-width:15px;background:${datum.color};width:12px;height:12px;border-radius:50%;margin-top:6px;grid-row-start:1;grid-column-start:1;"></div>
        <p style="margin:0;padding:0;grid-column-start:2;grid-row-start:1;">${capitalize(datum.category)}</p>
        <p style="margin:0;padding:0;grid-column-start:2;grid-row-start:2;">${amount}</p>
      </div>`;
  };
};

const TOOLTIP_CURSOR_GAP = 12;

// Place the card left of the cursor and centred on it, rather than ECharts' default
// below-the-cursor placement. The canvas is a fixed 800px so drilldown animation stays smooth
// (SIMPL-34835), while the visible wrapper is only `rows * 28 + 30` tall and clips — so `confine`,
// which measures the canvas, never flips the card up and a single-bar drilldown renders it out of
// view. `confine: true` from buildTooltipConfig still runs after this and clamps x/y to >= 0.
const buildTooltipPosition = (
  point: [number, number],
  _params: unknown,
  _dom: unknown,
  _rect: unknown,
  size: { contentSize: [number, number]; viewSize: [number, number] },
): [number, number] => {
  const [pointerX, pointerY] = point;
  const [contentWidth, contentHeight] = size.contentSize;
  return [pointerX - contentWidth - TOOLTIP_CURSOR_GAP, pointerY - contentHeight / 2];
};

export const buildSidewaysBarChartOption = (
  data: SidewaysBarDatum[],
  config: SidewaysBarChartConfig,
): EChartsOption => {
  const { theme, animated = true, minValue, maxValue } = config;

  const categories = data.map((d) => d.category);

  // This echarts build does not register `LegacyGridContainLabel`, so `grid.containLabel`
  // is unsupported and collapses the plot. Reserve left space for the category labels
  // ourselves: estimate the widest label (Rubik ~7.5px/char at 14px), capped like amcharts4's
  // maxWidth = 250. Labels beyond the cap truncate.
  const LABEL_CAP = 250;
  const longestLabelChars = categories.reduce((max, c) => Math.max(max, capitalize(c).length), 0);
  const labelWidth = Math.min(LABEL_CAP, Math.max(40, Math.ceil(longestLabelChars * 7.5) + 8));

  // Value labels ("$1,240.55") are wider per char than category text — the $ , . glyphs push the
  // average up, so estimate them at ~9px/char in Rubik@14 to avoid clipping/overlap.
  const VALUE_LABEL_CHAR_PX = 9;

  // The largest positive bar reaches the value-axis max (right edge), so its value label sits in
  // the right margin. Reserve enough room for the widest label so it isn't clipped (amcharts4 used
  // a flat 60px padding, which truncates long amounts).
  const positiveLabelChars = data.reduce((max, d) => (d.value >= 0 ? Math.max(max, d.label.length) : max), 0);
  const rightPad = Math.max(60, Math.ceil(positiveLabelChars * VALUE_LABEL_CHAR_PX) + 16);

  // Negative bars extend left toward the axis min; their labels sit left of the bar tip. Reserve a
  // matching gap between the category labels and the plot so those labels don't overlap the
  // category names (only when negatives are present — otherwise it's just wasted whitespace).
  const negativeLabelChars = data.reduce((max, d) => (d.value < 0 ? Math.max(max, d.label.length) : max), 0);
  const negPad = negativeLabelChars > 0 ? Math.ceil(negativeLabelChars * VALUE_LABEL_CHAR_PX) + 20 : 0;
  // The canvas is stable for animation while the surrounding viewport grows with the row count.
  // Keep the grid itself row-sized so expanded report lists do not compress their bars.
  const gridHeight = Math.max(30, data.length * 28);

  return {
    ...buildAnimationConfig(animated),
    // Drill-down replaces labels, domain bounds, and often chart height. Let the bars animate, but
    // update the surrounding coordinate system immediately so it does not compete for frames.
    animationDurationUpdate: animated ? 300 : 0,
    animationEasingUpdate: 'cubicOut',
    textStyle: { fontFamily: theme.fontFamily },
    // Left reserves category-label space (+ negative value-label gap); right leaves room for
    // positive bar-end value labels.
    grid: { left: labelWidth + 8 + negPad, right: rightPad, top: 8, height: gridHeight },
    tooltip: {
      ...buildTooltipConfig(theme),
      trigger: 'item' as const,
      formatter: buildTooltipFormatter(data, theme, config),
      position: buildTooltipPosition,
    },
    // Horizontal bars: category on the y-axis (inversed to match amcharts4), value on x.
    yAxis: {
      type: 'category' as const,
      animation: false,
      inverse: true,
      data: categories,
      axisLabel: {
        color: theme.textSecondaryColor,
        fontSize: 14,
        width: labelWidth,
        overflow: 'truncate' as const,
        // The y-axis sits at grid.left, so its labels and the most-negative bar's value label are
        // both anchored there. Push category labels left past the negative-label zone (+8 default)
        // so they don't stack. Collapses to the default 8 when there are no negatives.
        margin: negPad + 8,
        formatter: (value: string) => capitalize(value),
      },
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: false },
    },
    xAxis: {
      type: 'value' as const,
      animation: false,
      min: minValue,
      max: maxValue,
      axisLabel: { show: false },
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: false },
    },
    series: buildSidewaysBarSeries(data, config, theme),
  };
};
